/**
 * The `<head>` of one language's landing page.
 *
 * Shared by `routes/index.tsx` (English, at `/`) and `routes/$lang.tsx` (the
 * other eight). Two routes render the same page and therefore have to make the
 * same claims about it — a canonical or an hreflang set that differs between
 * them is precisely the bug that makes Google drop the whole language cluster,
 * and it is invisible in a browser.
 */

import {
  alternates,
  appJsonLd,
  canonical,
  faqJsonLd,
  freeToolsJsonLd,
  organizationJsonLd,
  socialMeta,
  websiteJsonLd,
} from "../seo";
import { toolsIndexPath } from "../tools/index";
import type { ContentLang } from "./registry";
import { langPath } from "./registry";
import type { MarketingBundle } from "./types";

/**
 * What the site and the app are CALLED, as opposed to what the page is titled.
 *
 * Both entity blocks were fed `bundle.title`, which made the app's schema.org
 * name "Fazly — AI Face Analysis and Glow-Up Plan" and the WebSite's the same.
 * That was merely odd while the title was a description. It became wrong the
 * moment the title started carrying search terms ("Free AI Face Test,
 * Looksmax Score"): a `MobileApplication` named "Free AI Face Test" claims the
 * APP is a free test, which is the one thing on this page that is not. And
 * Google reads `WebSite.name` as the site name it prints above every result,
 * where a sixty-character title is exactly what it is documented to reject.
 * The entities are named Fazly; the title does the searching.
 */
const SITE_NAME = "Fazly";

/**
 * The tools hub's H1, per language, for the free-tools entity.
 *
 * Mirrored here rather than read from `TOOLS_UI` because the landing chunk
 * does not otherwise load the tools UI bundle — all five languages of it, about
 * 17 KB — and a structured-data name is not worth that on the page whose LCP
 * is the one the AI channel lands on. `marketing.test.ts` asserts each value is
 * byte-identical to `TOOLS_UI[lang].indexH1`, so the two cannot drift.
 */
export const FREE_TOOLS_NAME: Record<ContentLang, string> = {
  en: "Free face analysis tools",
  tr: "Ücretsiz yüz analizi araçları",
  de: "Kostenlose Gesichtsanalyse-Tools",
  es: "Herramientas gratis de análisis facial",
  pt: "Ferramentas grátis de análise facial",
};

export function landingHead(lang: ContentLang, bundle: MarketingBundle) {
  const path = langPath(lang);
  return {
    meta: [
      { title: bundle.title },
      { name: "description", content: bundle.description },
      ...socialMeta({
        title: bundle.title,
        description: bundle.description,
        path,
        lang,
        imageAlt: bundle.ogTagline,
      }),
      // Emitted as `<script type="application/ld+json">` by TanStack's head
      // renderer, which also escapes it — see `headContentUtils`. Three separate
      // blocks rather than one `@graph`: Google's Rich Results Test reports per
      // block, so a mistake in the FAQ does not take the app entity down with it.
      { "script:ld+json": appJsonLd(SITE_NAME, bundle.description) },
      {
        "script:ld+json": freeToolsJsonLd({
          name: FREE_TOOLS_NAME[lang],
          description: bundle.tools.body,
          path: toolsIndexPath(lang),
          lang,
        }),
      },
      { "script:ld+json": faqJsonLd(bundle.faq.items) },
      { "script:ld+json": organizationJsonLd() },
      // The site as an entity, tying the app, the blog and the legal pages to
      // one publisher — see `websiteJsonLd` for why it declares no SearchAction.
      { "script:ld+json": websiteJsonLd(SITE_NAME, bundle.description) },
    ],
    links: [
      { rel: "canonical", href: canonical(path) },
      ...alternates(langPath),
    ],
  };
}
